import React, { useState, useEffect, useContext } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { organizerService, eventService } from '../services/api';
import EventCard from '../components/EventCard';
import '../styles/OrganizerPage.css';

const OrganizerPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();
  const [organizerData, setOrganizerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const validTabs = ['upcoming', 'past'];
  const [eventTab, setEventTabState] = useState(() => {
    const tab = searchParams.get('tab');
    return validTabs.includes(tab) ? tab : 'upcoming';
  });
  const [statusFilter, setStatusFilter] = useState('approved');
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [formData, setFormData] = useState({
    organizerName: '',
    description: '',
    website: '',
    instagram: '',
    tiktok: ''
  });
  const setEventTab = (tab) => {
    setEventTabState(tab);
    setSearchParams({ tab });
  };
  
  useEffect(() => {
    fetchOrganizer();
  }, [id]);

  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenuId]);

  const fetchOrganizer = async () => {
    try {
      const { data } = await organizerService.getOrganizer(id);
      setOrganizerData(data);
      setFormData({
        organizerName: data.organizer.organizerName || data.organizer.profileName || '',
        description: data.organizer.description || '',
        website: data.organizer.website || '',
        instagram: data.organizer.instagram || '',
        tiktok: data.organizer.tiktok || ''
      });
    } catch (error) {
      console.error('Error fetching organizer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate URLs if provided
    if (formData.instagram && !formData.instagram.startsWith('http')) {
      showToast('Instagram muss eine vollständige URL sein (z.B. https://instagram.com/username)', 'error');
      return;
    }
    if (formData.tiktok && !formData.tiktok.startsWith('http')) {
      showToast('TikTok muss eine vollständige URL sein (z.B. https://tiktok.com/@username)', 'error');
      return;
    }

    try {
      await organizerService.updateOrganizer(id, formData);
      setEditing(false);
      fetchOrganizer();
      showToast('Profil erfolgreich aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating organizer:', error);
      showToast('Fehler beim Aktualisieren des Profils', 'error');
    }
  };

  const handleDeleteEvent = async () => {
    if (!deletingEventId) return;
    setDeleteLoading(true);
    try {
      await eventService.deleteEvent(deletingEventId);
      setDeletingEventId(null);
      showToast('Event wurde gelöscht', 'success');
      fetchOrganizer();
    } catch (error) {
      showToast('Fehler beim Löschen des Events', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) return <div className="loading">Veranstalter wird geladen...</div>;
  if (!organizerData) return <div className="error">Veranstalter nicht gefunden</div>;

  const { organizer, events } = organizerData;
  const isOwnProfile = user && String(user._id || user.id) === String(id);
  const isAdmin = user && user.role === 'admin';
  const canEdit = isOwnProfile;

  // Get display name: organizerName for verified users, profileName for others
  const displayName = organizer.isVerified && organizer.organizerName
    ? organizer.organizerName
    : (organizer.profileName || organizer.organizerName || 'Unbekannt');

  // Filter events based on current date
  // Events are past only if the day after endDate has arrived
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of today

  const isOwnOrAdmin = isOwnProfile || isAdmin;

  const filteredByStatus = isOwnOrAdmin
    ? events.filter(event => event.status === statusFilter)
    : events.filter(event => event.status === 'approved');

  const upcomingEvents = filteredByStatus.filter(event => {
    const eventEndDate = new Date(event.endDate);
    eventEndDate.setHours(0, 0, 0, 0);
    return eventEndDate >= today;
  });

  const pastEvents = filteredByStatus.filter(event => {
    const eventEndDate = new Date(event.endDate);
    eventEndDate.setHours(0, 0, 0, 0);
    return eventEndDate < today;
  });

  const displayedEvents = eventTab === 'upcoming' ? upcomingEvents : pastEvents;

  return (
    <div className="organizer-page">
      <div className="organizer-header">
        <h1>
          {displayName}
          {organizer.isVerified && (
            <span className="verified-badge" title="Verifizierter Veranstalter">✓</span>
          )}
        </h1>
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)} className="btn btn-secondary">
            Profil bearbeiten
          </button>
        )}
      </div>

      
      {editing ? (
        <form onSubmit={handleSubmit} className="organizer-form">
          <div className="form-group">
            <label>Veranstaltername</label>
            <input
              type="text"
              name="organizerName"
              value={formData.organizerName}
              onChange={handleChange}
              required
            />
          </div>

          {organizer.isVerified && (
            <>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Webseite</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Instagram</label>
                <input
                  type="text"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  placeholder="https://instagram.com/username"
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Vollständige URL eingeben (z.B. https://instagram.com/username)
                </small>
              </div>

              <div className="form-group">
                <label>TikTok</label>
                <input
                  type="text"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleChange}
                  placeholder="https://tiktok.com/@username"
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Vollständige URL eingeben (z.B. https://tiktok.com/@username)
                </small>
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">Änderungen speichern</button>
            <button type="button" onClick={() => setEditing(false)} className="btn btn-secondary">
              Abbrechen
            </button>
          </div>
        </form>
      ) : (
        <>
          {organizer.isVerified && (organizer.description || organizer.website || organizer.instagram || organizer.tiktok) && (
            <div className="organizer-info">
              {organizer.description && (
                <div className="info-section">
                  <h2>Über uns</h2>
                  <p>{organizer.description}</p>
                </div>
              )}

              {(organizer.website || organizer.instagram || organizer.tiktok) && (
                <div className="info-section contact-info">
                  {organizer.website && (
                    <div>
                      <strong>Webseite:</strong> <a href={organizer.website} target="_blank" rel="noopener noreferrer">{organizer.website}</a>
                    </div>
                  )}
                  {organizer.instagram && (
                    <div>
                      <strong>Instagram:</strong> <a href={organizer.instagram} target="_blank" rel="noopener noreferrer">{organizer.instagram}</a>
                    </div>
                  )}
                  {organizer.tiktok && (
                    <div>
                      <strong>TikTok:</strong> <a href={organizer.tiktok} target="_blank" rel="noopener noreferrer">{organizer.tiktok}</a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div className="organizer-events">
        <h2>Events von {displayName}</h2>

        {isOwnOrAdmin && (
          <div className="status-filter">
            {['approved', 'pending', 'rejected'].map(s => (
              <button
                key={s}
                className={`status-filter-btn ${statusFilter === s ? 'active' : ''} status-filter-btn--${s}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'approved' ? 'Genehmigt' : s === 'pending' ? 'Ausstehend' : 'Abgelehnt'}
              </button>
            ))}
          </div>
        )}

        {events.length === 0 ? (
          <p>Keine Events verfügbar.</p>
        ) : (
          <>
            <div className="event-tabs">
              <button
                className={`event-tab-button ${eventTab === 'upcoming' ? 'active' : ''}`}
                onClick={() => setEventTab('upcoming')}
              >
                Kommende Events ({upcomingEvents.length})
              </button>
              <button
                className={`event-tab-button ${eventTab === 'past' ? 'active' : ''}`}
                onClick={() => setEventTab('past')}
              >
                Vergangene Events ({pastEvents.length})
              </button>
            </div>

            {displayedEvents.length === 0 ? (
              <p className="no-events-message">
                {eventTab === 'upcoming'
                  ? 'Keine kommenden Events vorhanden.'
                  : 'Keine vergangenen Events vorhanden.'}
              </p>
            ) : (
              <div className="events-grid">
                {displayedEvents.map((event) => (
                  <div key={event._id} className="event-card-wrapper">
                    <EventCard event={event} showStatus={isOwnProfile || isAdmin} />
                    {isOwnOrAdmin && (
                      <div className="event-card-menu" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="event-card-menu-btn"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenMenuId(openMenuId === event._id ? null : event._id); }}
                          title="Optionen"
                        >
                          <span></span><span></span><span></span>
                        </button>
                        {openMenuId === event._id && (
                          <div className="event-card-dropdown">
                            <button
                              className="event-card-dropdown-item event-card-dropdown-item--danger"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenMenuId(null); setDeletingEventId(event._id); }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                <path d="M10 11v6"></path><path d="M14 11v6"></path>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                              </svg>
                              Löschen
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {deletingEventId && (
        <div className="delete-modal-overlay" onClick={() => !deleteLoading && setDeletingEventId(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Event löschen?</h3>
            <p>Das Event und alle zugehörigen Daten (Bild, Benachrichtigungen) werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="delete-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setDeletingEventId(null)}
                disabled={deleteLoading}
              >
                Abbrechen
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteEvent}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Wird gelöscht…' : 'Endgültig löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerPage;
